// Renders Sentinel reject, assign, and feedback modals.

import { useEffect, useMemo, useState } from 'react';
import { LoaderCircle, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { MultiSelect } from '@/components/ui/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { SentinelWorkspaceAdapter } from '@/adapters/sentinelWorkspace.adapter';
import { sentinelWorkspaceService } from '@/services/sentinelWorkspace.service';
import { toast } from '@/components/ui/sonner';
import { TicketFieldsPicker } from '@/components/shared/ticket-fields-picker';
import { useHelpdeskTicketFields } from '@/hooks/helpdesk-ticket-fields-query';
import type { TicketFieldValue } from '@/types';
import {
  OTHERS_CATEGORY,
  OTHERS_REJECT_REASON,
  REFINE_CATEGORIES,
  REJECT_REASONS,
  REMEMBER_SCOPES,
  type AgentOption,
  type FeedbackVerdict,
  type HelpdeskTagDto,
  type RejectReason,
} from './types';

function ModalShell({
  open,
  onClose,
  title,
  subtitle,
  icon,
  className,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      {/* Solid fill: the base frosted style lets the conversation bleed through when the blur is skipped. */}
      <DialogContent
        className={cn('w-[400px] gap-0 overflow-visible rounded-[20px] p-0', className)}
        showCloseButton={false}
      >
        {/* min-w-0: a long select label would otherwise stretch the dialog past its own width. */}
        {/* Same 16px inset as the sections below, so the title lines up with them. */}
        <div className="flex min-h-[50px] min-w-0 items-center gap-2.5 rounded-t-[20px] border-b border-gray-500 px-4 py-3">
          {icon && <TriangleAlert className="size-6 text-red-500" />}
          <div className="flex flex-col gap-0.5">
            <DialogTitle className="text-[14px] font-semibold leading-5 text-text-body">{title}</DialogTitle>
            {subtitle && <p className="text-[12px] leading-4 text-gray-700">{subtitle}</p>}
          </div>
        </div>
        <div className="flex min-w-0 flex-col">{children}</div>
      </DialogContent>
    </Dialog>
  );
}

function ModalActions({
  onCancel,
  onConfirm,
  confirmLabel,
  confirmTone,
  confirmDisabled,
  confirmBusy,
}: {
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel: string;
  confirmTone: 'red' | 'black';
  confirmDisabled?: boolean;
  confirmBusy?: boolean;
}) {
  return (
    <div className="flex items-center justify-end gap-1.5 p-3">
      <Button variant="outline" onClick={onCancel} disabled={confirmBusy} className="text-[13px] font-normal">
        Cancel
      </Button>
      <Button
        onClick={onConfirm}
        disabled={confirmDisabled || confirmBusy}
        className={cn(
          'text-[13px] font-normal',
          confirmTone === 'red' && 'border-red-500 bg-red-500 hover:bg-red-600',
        )}
      >
        {confirmBusy && <LoaderCircle className="size-[14px] animate-spin" />}
        {confirmBusy ? 'Confirming' : confirmLabel}
      </Button>
    </div>
  );
}

export type RejectModalVariant = 'rejectBlueprint' | 'rejectResponse' | 'assign';

/** The agent choice that hands nobody the ticket; Radix Select cannot clear back to empty. */
const LEAVE_UNASSIGNED = 'leave-unassigned';

const REJECT_COPY: Record<RejectModalVariant, { title: string; body: string; assignLabel: string; tone: 'red' | 'black'; icon: boolean }> = {
  rejectBlueprint: {
    title: 'Reject & Unassign?',
    body: 'Do you really want to reject the blueprint and unassign this ticket?',
    assignLabel: 'Would you like to assign it to someone else?',
    tone: 'red',
    icon: true,
  },
  rejectResponse: {
    title: 'Reject & Unassign?',
    body: 'Are you sure you want to reject the AI Suggested response and drop the ticket?',
    assignLabel: 'Would you like to assign it to someone else?',
    tone: 'black',
    icon: true,
  },
  assign: {
    title: 'Assign to others ?',
    body: 'By unassigning this ticket, you will no longer be responsible for it.',
    assignLabel: 'Assign to',
    tone: 'black',
    icon: false,
  },
};

/** After a failed generation there is no AI suggestion to reject, only the ticket to hand back. */
const REJECT_AFTER_ERROR_BODY = 'Sentinel could not answer this ticket. Are you sure you want to unassign it?';

/** What the agent leaves on the helpdesk ticket as they let go of it. */
export interface RejectHandoff {
  tags: string[];
  note: string | null;
}

interface RejectModalProps {
  open: boolean;
  variant: RejectModalVariant;
  ticketId: number;
  offersProviderAgent: boolean;
  /** Opened from a failed generation; the body then speaks of the ticket, not an AI suggestion. */
  afterError?: boolean;
  onClose: () => void;
  onConfirm: (
    reason: RejectReason,
    providerAgentId: string | null,
    otherText: string | null,
    handoff: RejectHandoff,
  ) => void | Promise<void>;
}

export function RejectModal({ open, variant, ticketId, offersProviderAgent, afterError = false, onClose, onConfirm }: RejectModalProps) {
  const copy = REJECT_COPY[variant];
  const [reason, setReason] = useState<RejectReason | ''>('');
  const [otherText, setOtherText] = useState('');
  const [agentId, setAgentId] = useState<string>('');
  const [agents, setAgents] = useState<AgentOption[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  // The helpdesk's own tags, offered as suggestions; a helpdesk without a list leaves this empty.
  const [tagSuggestions, setTagSuggestions] = useState<HelpdeskTagDto[]>([]);
  const tagOptions = useMemo(
    () => tagSuggestions.map(({ name, color }) => ({ label: name, value: name, style: { badgeColor: color ?? undefined } })),
    [tagSuggestions],
  );
  const [note, setNote] = useState('');
  // Confirming rejects the ticket and reassigns it, so the button has to hold until that finishes.
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason('');
    setOtherText('');
    setAgentId('');
    setAgents([]);
    setTags([]);
    setNote('');
    setSubmitting(false);
    // Suggestions only: a failed load still lets the agent type a tag.
    sentinelWorkspaceService
      .getTicketTags(ticketId)
      .then((page) => setTagSuggestions(page.tags))
      .catch(() => setTagSuggestions([]));
    if (!offersProviderAgent) return;
    setAgentsLoading(true);
    sentinelWorkspaceService
      .getProviderAgents(ticketId)
      .then((page) => setAgents(SentinelWorkspaceAdapter.agentsFromApi(page.agents)))
      .catch(() => toast.error('Unable to load helpdesk agents. Please try again.'))
      .finally(() => setAgentsLoading(false));
  }, [open, offersProviderAgent, ticketId]);

  const submit = async () => {
    if (!reason || submitting) return;
    setSubmitting(true);
    try {
      const pickedAgent = agentId && agentId !== LEAVE_UNASSIGNED ? agentId : null;
      await onConfirm(reason, pickedAgent, otherText.trim() || null, { tags, note: note.trim() || null });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell open={open} onClose={onClose} title={copy.title} icon={copy.icon}>
      <div className="flex flex-col gap-5 px-4 py-3">
        <p className="text-[13px] leading-[18px] text-text-body">{afterError ? REJECT_AFTER_ERROR_BODY : copy.body}</p>
        <div className="h-px w-full bg-gray-500" />
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <p className="text-[12px] font-medium leading-4 text-gray-900">Reason</p>
            <Select value={reason} onValueChange={(value) => setReason(value as RejectReason)}>
              <SelectTrigger className="h-10 w-full rounded-xl text-[13px]">
                <SelectValue placeholder="Choose a reason for unassigning" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                {REJECT_REASONS.map(({ value, label }) => (
                  <SelectItem key={value} value={value} className="text-[13px]">
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {reason === OTHERS_REJECT_REASON && (
              <textarea
                autoFocus
                value={otherText}
                onChange={(event) => setOtherText(event.target.value)}
                placeholder="Mention your reason..."
                className="h-20 w-full resize-none rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-[13px] leading-[18px] text-text-body outline-none placeholder:text-gray-700"
              />
            )}
          </div>
          {offersProviderAgent && (
            <div className="flex flex-col gap-2">
              <p className="text-[12px] font-medium leading-4 text-gray-900">{copy.assignLabel}</p>
              <Select value={agentId} onValueChange={setAgentId} disabled={agentsLoading}>
                {/* Radix drops SelectValue's own className, so size the value from the trigger. */}
                <SelectTrigger className="h-10 w-full min-w-0 rounded-xl text-[13px] *:data-[slot=select-value]:min-w-0 *:data-[slot=select-value]:flex-1">
                  <SelectValue placeholder={agentsLoading ? 'Loading agents...' : 'Choose an Agent (optional)'} />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value={LEAVE_UNASSIGNED} className="text-[13px]">
                    Leave unassigned
                  </SelectItem>
                  {agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id} className="text-[13px]">
                      {agent.name}{agent.email ? ` (${agent.email})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(!agentId || agentId === LEAVE_UNASSIGNED) && (
                <p className="text-[12px] leading-4 text-gray-700">Without an agent, the ticket goes back to the helpdesk unassigned.</p>
              )}
            </div>
          )}
          {/* Only the helpdesk's own tags can be picked, so a helpdesk without a tag list shows no field. */}
          {tagOptions.length > 0 && (
            <div className="flex flex-col gap-2">
              <p className="text-[12px] font-medium leading-4 text-gray-900">Tags</p>
              <MultiSelect
                options={tagOptions}
                defaultValue={tags}
                onValueChange={setTags}
                placeholder="Add tags for the helpdesk ticket"
                popoverPlaceholder="Search tags"
                showAllSelected
                hideSelectAll
              />
            </div>
          )}
          <div className="flex flex-col gap-2">
            <p className="text-[12px] font-medium leading-4 text-gray-900">Private note</p>
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Leave a note for the next agent..."
              aria-label="Private note"
              className="h-20 w-full resize-none rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-[13px] leading-[18px] text-text-body outline-none placeholder:text-gray-700"
            />
          </div>
        </div>
      </div>
      <ModalActions
        onCancel={onClose}
        onConfirm={() => void submit()}
        confirmLabel="Confirm"
        confirmTone={copy.tone}
        confirmDisabled={!reason}
        confirmBusy={submitting}
      />
    </ModalShell>
  );
}

interface FeedbackForAiModalProps {
  open: boolean;
  /** The reply also closes a live Gorgias ticket, so its ticket fields are asked for first. */
  closeTicketFields?: boolean;
  onClose: () => void;
  subtitle?: string;
  /** Off for an automated reply: there is no draft to remember, only a verdict on what was sent. */
  askScope?: boolean;
  onSubmit: (
    verdict: FeedbackVerdict,
    reasons: string[],
    otherText: string | null,
    rememberScope: string | null,
    ticketFields: TicketFieldValue[],
  ) => void | Promise<void>;
}

/**
 * The helpdesk's ticket fields to set before the close. Gorgias refuses to close a ticket while a
 * required field is empty, so the reply cannot be sent until each one has a value.
 */
function CloseTicketFields({
  value,
  onChange,
  onBlockingChange,
}: {
  value: TicketFieldValue[];
  onChange: (next: TicketFieldValue[]) => void;
  onBlockingChange: (blocking: boolean) => void;
}) {
  const { data, isLoading, isError, isFetching, refetch } = useHelpdeskTicketFields();
  const fields = (data?.available && data.fields) || [];
  // Unknown fields block too: sent without a required one, the reply goes out and Gorgias refuses the close.
  const unknown = isLoading || isError;
  const blocking =
    unknown || fields.some((field) => field.required && !value.some((entry) => entry.fieldId === field.id));

  useEffect(() => onBlockingChange(blocking), [blocking, onBlockingChange]);

  // Nothing to fill: the close goes ahead as it always has.
  if (!unknown && fields.length === 0) return null;

  return (
    <div className="flex flex-col gap-2 pt-4">
      <div className="h-px w-full bg-gray-300" />
      <div className="flex flex-col gap-1 px-4 pt-1">
        <p className="text-[13px] font-medium leading-[18px] text-gray-900">Ticket fields</p>
        <p className="text-[12px] leading-4 text-gray-700">Set on the ticket right before it is closed.</p>
        {isLoading ? (
          <p className="py-2 text-[13px] leading-[18px] text-gray-700">Loading ticket fields…</p>
        ) : isError ? (
          <div className="flex items-center justify-between gap-2 py-2">
            <p className="text-[13px] leading-[18px] text-gray-700">Couldn't load the ticket fields.</p>
            <Button variant="outline" size="sm" disabled={isFetching} onClick={() => void refetch()}>
              Retry
            </Button>
          </div>
        ) : (
          <TicketFieldsPicker fields={fields} value={value} onChange={onChange} />
        )}
      </div>
    </div>
  );
}

/** Feedback for AI shown before sending a reply. */
export function FeedbackForAiModal({
  open,
  closeTicketFields = false,
  onClose,
  onSubmit,
  subtitle = 'Was this response good?',
  askScope = true,
}: FeedbackForAiModalProps) {
  const [verdict, setVerdict] = useState<FeedbackVerdict | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [otherText, setOtherText] = useState('');
  const [scope, setScope] = useState('');
  const [ticketFields, setTicketFields] = useState<TicketFieldValue[]>([]);
  const [fieldsBlocking, setFieldsBlocking] = useState(false);
  // Submitting also sends the reply, so the button has to hold until that finishes.
  const [submitting, setSubmitting] = useState(false);

  // Fresh sheet every time the modal opens.
  useEffect(() => {
    if (!open) {
      setVerdict(null);
      setSelected([]);
      setOtherText('');
      setScope('');
      setTicketFields([]);
      setFieldsBlocking(false);
      setSubmitting(false);
    }
  }, [open]);

  // A verdict and a remember scope are required; a "no" also needs a reason, and Others needs its text.
  const incomplete =
    (closeTicketFields && fieldsBlocking) ||
    verdict === null ||
    (askScope && !scope) ||
    (verdict === 'no' && (selected.length === 0 || (selected.includes(OTHERS_CATEGORY) && !otherText.trim())));

  const submit = async () => {
    if (submitting || incomplete || verdict === null) return;
    setSubmitting(true);
    try {
      const reasons = verdict === 'no' ? selected : [];
      await onSubmit(
        verdict,
        reasons,
        verdict === 'no' ? otherText.trim() || null : null,
        scope || null,
        closeTicketFields ? ticketFields : [],
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Feedback for AI"
      subtitle={subtitle}
      className={closeTicketFields ? 'w-[520px]' : undefined}
    >
      <RadioGroup
        value={verdict ?? ''}
        onValueChange={(value) => setVerdict(value === 'yes' || value === 'no' ? value : null)}
        className="flex justify-center gap-8 px-4 pt-4"
      >
        {(['yes', 'no'] as const).map((option) => (
          <label key={option} className="flex cursor-pointer items-center gap-2 text-[14px] leading-5 text-text-body">
            <RadioGroupItem
              value={option}
              className="size-[14px] border-gray-500 bg-white shadow-none data-[state=checked]:border-indigo-500 data-[state=checked]:bg-indigo-500 [&_svg]:size-2 [&_svg]:fill-white"
            />
            {option === 'yes' ? 'Yes' : 'No'}
          </label>
        ))}
      </RadioGroup>
      {verdict === 'no' && (
      <div className="flex flex-col gap-1.5 px-4 pt-4">
        <p className="text-[13px] font-medium leading-[18px] text-gray-900">Select whatever is relevant</p>
        {REFINE_CATEGORIES.map((reason) => (
          <label
            key={reason}
            className="flex cursor-pointer items-center gap-3 py-0.5 text-[14px] leading-5 text-text-body"
          >
            <Checkbox
              className="size-[14px] border-gray-500 bg-white shadow-none data-[state=checked]:border-indigo-500 data-[state=checked]:bg-indigo-500 [&_svg]:size-3"
              checked={selected.includes(reason)}
              onCheckedChange={(checked) =>
                setSelected((prev) => (checked ? [...prev, reason] : prev.filter((item) => item !== reason)))
              }
            />
            {reason}
          </label>
        ))}
        {selected.includes(OTHERS_CATEGORY) && (
          <textarea
            autoFocus
            maxLength={2000}
            value={otherText}
            onChange={(event) => setOtherText(event.target.value)}
            placeholder="Tell us what went wrong..."
            className="mt-1.5 h-20 w-full resize-none rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-[13px] leading-[18px] text-text-body outline-none placeholder:text-gray-700"
          />
        )}
      </div>
      )}
      {askScope && (
        <div className="flex flex-col gap-2 pt-4">
          <div className="h-px w-full bg-gray-300" />
          <div className="flex flex-col gap-2 px-4 pt-1">
            <p className="text-[13px] font-medium leading-[18px] text-gray-900">Remember this</p>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="h-10 w-full rounded-xl text-[13px]">
                <SelectValue placeholder="Choose an option" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                {REMEMBER_SCOPES.map((option) => (
                  <SelectItem key={option} value={option} className="text-[13px]">
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
      {open && closeTicketFields && (
        <CloseTicketFields value={ticketFields} onChange={setTicketFields} onBlockingChange={setFieldsBlocking} />
      )}
      {/* Figma has no Cancel here — closing the modal is the cancel. */}
      <div className="flex items-center justify-end px-4 pb-4 pt-3">
        <Button
          onClick={() => void submit()}
          disabled={submitting || incomplete}
          className="text-[13px] font-normal"
        >
          {submitting && <LoaderCircle className="size-[14px] animate-spin" />}
          {submitting ? 'Submitting' : 'Submit'}
        </Button>
      </div>
    </ModalShell>
  );
}
